<template>
    <Dropdown expanded>
        <template #trigger="{ open }">
            <slot name="trigger" :open="open" :selected-tags="selectedTags">
                <Button :active="open" :glass :loading="isLoading" class="tw-flex tw-gap-1">
                    <i class="mdi mdi-tune-variant"></i>
                    Tags
                    <span
                        v-if="selectedTagIds.length"
                        class="tw-border tw-border-gray-400 tw-ml-1 tw-w-5 tw-h-5 tw-flex tw-justify-center tw-items-center tw-rounded-full tw-text-xs tw-font-normal"
                    >
                        {{ selectedTagIds.length }}
                    </span>
                </Button>
            </slot>
        </template>
        <div
            v-if="tagFolders.length || uncategorisedTags.length"
            class="tw-overflow-x-auto tw-flex tw-flex-nowrap tw-text-nowrap tw-min-h-[100px] [&::-webkit-scrollbar]:tw-h-1.5 [&::-webkit-scrollbar-track]:tw-bg-transparent [&::-webkit-scrollbar-thumb]:tw-rounded-full [&::-webkit-scrollbar-thumb]:tw-bg-gray-200 hover:[&::-webkit-scrollbar-thumb]:tw-bg-gray-300"
        >
            <div v-if="uncategorisedTags.length" class="tw-w-max tw-shrink-0 tw-py-2 tw-border-r tw-flex tw-flex-col">
                <span class="tw-font-semibold tw-text-xs tw-text-gray-500 tw-px-3 tw-pb-1">UNCATEGORISED</span>
                <Checkbox
                    v-for="tag in uncategorisedTags"
                    :key="tag.id"
                    v-model="selectedTagIds"
                    :value="tag.id"
                    class="tw-mx-3 tw-my-1.5"
                >
                    <GroupTag class="!tw-px-1 !tw-py-0" :tag="tag" />
                </Checkbox>
            </div>
            <div
                v-for="folder in tagFolders"
                :key="folder.id"
                class="tw-w-max tw-shrink-0 tw-py-2 tw-border-r tw-flex tw-flex-col"
            >
                <span
                    class="tw-font-semibold tw-text-xs tw-uppercase tw-text-gray-500 tw-px-3 tw-pb-1"
                    :style="{ color: folder.colour || '#6b7280' }"
                >
                    {{ folder.name }}
                </span>
                <Checkbox
                    v-for="tag in folder.group_tags"
                    :key="tag.id"
                    v-model="selectedTagIds"
                    :value="tag.id"
                    class="tw-mx-3 tw-my-1.5 tw-py-0.5"
                >
                    <GroupTag class="!tw-px-1 !tw-py-0" :tag="tag" />
                </Checkbox>
            </div>
        </div>
        <div v-else class="tw-text-xs tw-text-gray-500 tw-py-3 tw-px-4 tw-text-center">
            There aren&apos;t any tags to show here just yet.
            <br />
            You can set up tags and their folders in your account settings.
        </div>
        <div class="tw-bg-gray-50 tw-border-t tw-py-2 tw-px-3 tw-flex tw-items-center tw-justify-between">
            <span class="tw-text-xs tw-text-gray-500">
                {{ selectedTagIds.length ? selectedTagIds.length : defaultToAll ? 'All' : 'No' }} tag{{
                    selectedTagIds.length !== 1 ? 's' : ''
                }}
                selected
            </span>
            <Button compact :disabled="selectedTagIds.length === 0" @click="selectedTagIds = []">
                Clear selection
            </Button>
        </div>
    </Dropdown>
</template>
<script lang="ts" setup>
import Dropdown from '@/js/components/controls/Dropdown.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import Button from '@/js/components/controls/Button.vue';
import { computed, onMounted, ref } from 'vue';
import axios, { type AxiosError } from 'axios';
import type { GroupTag as GroupTagType } from '@/js/types/groupTag.ts';
import type { GroupTagFolder } from '@/js/types/groupTagFolder.ts';
import { toast } from '@/js/utils.js';
import { route } from 'ziggy-js';
import GroupTag from '@/js/components/GroupTag.vue';
import { useDataStore } from '@/js/stores/DataStore.js';

const selectedTagIds = defineModel<number[]>({
    required: true,
    default: () => [],
});
const uncategorisedTags = ref<GroupTagType[]>([]);
const tagFolders = ref<GroupTagFolder[]>([]);
const selectedTags = computed<GroupTagType[]>(() => {
    const availableTags = [
        ...uncategorisedTags.value,
        ...tagFolders.value.flatMap((folder) => folder.group_tags ?? []),
    ];

    return selectedTagIds.value.flatMap((tagId) => {
        const tag = availableTags.find((availableTag) => availableTag.id === tagId);

        return tag ? [tag] : [];
    });
});

const props = defineProps({
    defaultToAll: { type: Boolean, default: false },
    glass: { type: Boolean, default: false },
    tags: { type: Array as () => GroupTagFolder[], default: () => [] },
});

defineSlots<{
    trigger?: (props: { open: boolean; selectedTags: GroupTagType[] }) => unknown;
}>();

onMounted(() => {
    refreshData();
});

const isLoading = ref<boolean>(false);

const refreshData = async () => {
    isLoading.value = true;
    try {
        let data = null;
        if (!props.tags.length) {
            ({ data } = await axios.get(
                route('api.account.tags', {
                    account: useDataStore().get('account.id'),
                    folderised: 1,
                })
            ));
        } else {
            data = props.tags;
        }
        uncategorisedTags.value = data[0]?.group_tags || [];
        const { 0: _uncategorised, ...folderMap } = data;
        tagFolders.value = Object.values(folderMap);
    } catch (error) {
        const message =
            (error as AxiosError<{ message?: string }>).response?.data?.message ||
            'An error occurred while fetching tag folders.';
        toast('Error', message, 'error');
    } finally {
        isLoading.value = false;
    }
};
</script>
